import { useState, useEffect } from "react";
import { Box, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { API_BASE_URL } from "../../config/api";

const formatDate = (value) => {
  if (!value) return "N/A";
  try {
    const date = new Date(value);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    const dateString = String(value).split("T")[0];
    return dateString || value;
  }
};

const TransferBookings = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTransferBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/admin/transferBooking/all`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch transfer bookings");
      }

      const data = await response.json();
      if (data.subCode === 200 && Array.isArray(data.items)) {
        setRows(
          data.items.map((item) => ({
            ...item,
            id: item.id,
          }))
        );
      } else {
        setError("Failed to load transfer bookings: " + (data.message || "Unknown error"));
      }
    } catch (err) {
      setError(err.message);
      console.error("Transfer booking fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransferBookings();
  }, []);

  const columns = [
    {
      field: "createdAt",
      headerName: "Create Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "reservation_no",
      headerName: "Reservation No",
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "search_id",
      headerName: "Search ID",
      flex: 1.2,
      minWidth: 260,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-all", lineHeight: 1.5 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "vehicle_id",
      headerName: "Vehicle ID",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "passenger_name",
      headerName: "Passenger",
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <Box>
          {params.row.passenger_name} {params.row.passenger_surname}
        </Box>
      ),
    },
    {
      field: "passenger_gender",
      headerName: "Gender",
      flex: 0.7,
      minWidth: 90,
    },
    {
      field: "passenger_email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-all" }}>{params.value}</Box>
      ),
    },
    {
      field: "passenger_phone",
      headerName: "Contact Number",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "passenger_flight_number",
      headerName: "Flight No",
      flex: 0.9,
      minWidth: 110,
    },
    {
      field: "airlines",
      headerName: "Airline",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "return_airline",
      headerName: "Return Airline",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "return_flight_number",
      headerName: "Return Flight",
      flex: 0.9,
      minWidth: 110,
    },
    {
      field: "suitcase",
      headerName: "Suitcases",
      flex: 0.6,
      minWidth: 90,
    },
    {
      field: "small_bags",
      headerName: "Small bags",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "extra_requests",
      headerName: "Extra requests",
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "price_total",
      headerName: "Total",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const cur = params.row.price_currency || "";
        const v = params.value != null ? parseFloat(params.value) : 0;
        return `${cur} ${Number.isFinite(v) ? v.toFixed(2) : params.value}`;
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatDate(params.value),
    },
  ];

  if (loading) return <Box m="20px">Loading transfer bookings...</Box>;
  if (error) return <Box m="20px">Error: {error}</Box>;

  return (
    <Box m="20px">
      <Header title="TRANSFER BOOKINGS" subtitle="Managing transfer bookings" />
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
            py: "8px",
            fontSize: "1rem",
            lineHeight: 1.5,
            color: colors.grey[100],
          },
          "& .MuiDataGrid-row": {
            minHeight: "90px !important",
            maxHeight: "none !important",
            height: "auto !important",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? `${colors.primary[500]} !important`
                  : `rgba(0, 0, 0, 0.04) !important`,
            },
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
            fontSize: "1rem",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: "1rem",
            },
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          checkboxSelection
          rows={rows}
          columns={columns}
          autoHeight={false}
          disableRowSelectionOnClick
          disableColumnResize={false}
          initialState={{
            sorting: {
              sortModel: [{ field: "createdAt", sort: "desc" }],
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default TransferBookings;
